import axios from "axios";

// Create axios instance with default config
const axiosInstance = axios.create({
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

// Request interceptor to add temporary API key to all requests
axiosInstance.interceptors.request.use(
  (config) => {
    const apiKey = localStorage.getItem("apiKey");

    // Add temporary API key to headers if available
    if (apiKey) {
      config.headers["x-api-key"] = apiKey;
    } else {
      // If no API key and not on login page, log warning
      // Don't block the request as some endpoints might not need auth
      if (
        typeof window !== "undefined" &&
        !window.location.pathname.includes("/login")
      ) {
        console.warn(
          "API key not found in localStorage for request:",
          config.url
        );
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle 401/400 errors (API key expired or missing)
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const errorMessage =
      error.response?.data?.error || error.response?.data?.message || "";
    const requestUrl = error.config?.url || "";

    // DEBUG: Log all error details
    console.group("🔍 Axios Interceptor - Error Debug");
    console.log("Request URL:", requestUrl);
    console.log("Status Code:", status);
    console.log("Error Message:", errorMessage);
    console.log("Full Error Response:", error.response?.data);
    console.log(
      "Current Pathname:",
      typeof window !== "undefined" ? window.location.pathname : "N/A"
    );
    console.log(
      "API Key in localStorage:",
      localStorage.getItem("apiKey") ? "EXISTS" : "MISSING"
    );

    // Check if this is an external API request (preprod/prod) - don't logout for these
    const isExternalApiRequest =
      requestUrl.includes("/api/prod/") ||
      requestUrl.includes("/api/preprod/") ||
      requestUrl.startsWith("/api/prod") ||
      requestUrl.startsWith("/api/preprod");
    console.log("Is External API Request:", isExternalApiRequest);

    // Only check for API key errors if it's a backend API request
    if (!isExternalApiRequest) {
      const lowerMsg = errorMessage.toLowerCase();
      const lowerUrl = requestUrl.toLowerCase();

      // Check if it's the docs endpoint
      const isDocsEndpoint =
        lowerUrl.includes("/api/apiCollection/docs") ||
        lowerUrl.includes("/apicollection/docs");
      console.log("Is Docs Endpoint:", isDocsEndpoint);

      const isApiKeyError =
        status === 401 ||
        (status === 400 && lowerMsg.includes("api key") && !isDocsEndpoint);
      console.log("Is API Key Error (will trigger logout):", isApiKeyError);

      // Only clear localStorage if:
      // 1. It's an API key error (401 or 400 with generic \"api key\" issue,
      //    but NOT the docs \"API key is required\" case), and
      // 2. We actually have an API key stored (meaning it was invalid/expired, not missing).
      if (isApiKeyError) {
        const storedApiKey = localStorage.getItem("apiKey");
        console.log("Stored API Key exists:", !!storedApiKey);

        if (storedApiKey) {
          console.warn("⚠️ CLEARING localStorage - API key error detected");
          // API key expired or invalid - clear storage and redirect to login
          localStorage.removeItem("apiKey");
          localStorage.removeItem("isAuthenticated");
          localStorage.removeItem("user_role");
          localStorage.removeItem("api_credits");
          localStorage.removeItem("api_credits_last_refill");

          // Redirect to login if not already there
          if (
            typeof window !== "undefined" &&
            window.location.pathname !== "/login" &&
            window.location.pathname !== "/"
          ) {
            console.log("🔄 Redirecting to login in 1.5s...");
            // Small delay to allow error message to be displayed
            setTimeout(() => {
              window.location.href = "/login";
            }, 1500);
          }
        } else {
          console.log(
            "ℹ️ No API key in localStorage - not clearing (expected behavior)"
          );
        }
      } else {
        console.log(
          "ℹ️ Not an API key error or is docs endpoint - not clearing localStorage"
        );
      }
    } else {
      console.log("ℹ️ External API request - not checking for logout");
    }

    console.groupEnd();

    // For external API errors or non-API-key errors, just pass through the error
    return Promise.reject(error);
  }
);

export default axiosInstance;
